import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PhoneFrame from "@/components/PhoneFrame";
import { Sparkles, Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

/**
 * Handles the redirect from Emergent Managed Google Auth.
 *
 * The URL will look like:
 *   /auth/google/callback?next=/app/chat#session_id=abc123
 *
 * We synchronously read the fragment (hash) and exchange it for our JWT.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function GoogleCallback() {
  const nav = useNavigate();
  const { setAuthFromToken } = useAuth();
  const processedRef = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const url = new URL(window.location.href);
    const next = url.searchParams.get("next") || "/app/chat";
    const hash = url.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) { setError("Missing session_id from Google redirect"); return; }
    const session_id = decodeURIComponent(match[1]);

    (async () => {
      try {
        const { data } = await axios.post(`${API}/auth/google/session`, { session_id });
        setAuthFromToken(data.access_token, data.user);
        // wipe fragment so refresh doesn't retry
        window.history.replaceState(null, "", url.pathname);
        nav(next, { replace: true });
      } catch (e) {
        setError(e?.response?.data?.detail || "Google sign-in failed");
      }
    })();
  }, []); // eslint-disable-line

  return (
    <PhoneFrame>
      <div className="flex flex-col items-center justify-center h-full min-h-[100dvh] px-6 text-center">
        <div className="h-12 w-12 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center">
          <Sparkles className="h-6 w-6" />
        </div>
        {!error ? (
          <>
            <div className="mt-4 font-display text-lg">Signing you in…</div>
            <Loader2 className="h-5 w-5 mt-2 animate-spin text-muted-foreground" />
          </>
        ) : (
          <>
            <AlertCircle className="h-10 w-10 mt-4 text-destructive" />
            <div className="mt-2 font-display text-lg">Sign-in failed</div>
            <div className="text-sm text-muted-foreground mt-1">{error}</div>
            <button onClick={() => nav("/auth?mode=login", { replace: true })} className="mt-4 text-primary underline text-sm">Back to sign in</button>
          </>
        )}
      </div>
    </PhoneFrame>
  );
}
